import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OFContext = createContext(null);

export function OFProvider({ children }) {
  // Estados principais
  const [ofs, setOfs] = useState([]);
  const [operacoes, setOperacoes] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [operadores, setOperadores] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  
  // Permissões de operadores: {operador_id: [{operacao_id, categoria_id}]}
  const [permissoesOperadores, setPermissoesOperadores] = useState({});
  
  // Categorias especiais (filtradas das categorias)
  const categoriasEspeciais = useMemo(() => {
    return categorias.filter(c => c.especial === true);
  }, [categorias]);
  
  // Estados de seleção
  const [selectedOF, setSelectedOF] = useState(null);
  const [selectedOperacoes, setSelectedOperacoes] = useState([]);
  
  // Filtros
  const [filtros, setFiltros] = useState({
    searchTerm: '',
    estadoFilter: 'todos',
    marcaFilter: 'todos',
    sortBy: 'numero_of',
    sortDirection: 'desc'
  });

  // Carregar dados
  const loadOfs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/ofs`);
      setOfs(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar OFs:', error);
      toast.error('Erro ao carregar ordens de fabrico');
    }
  }, []);

  const loadOperacoes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/operacoes`);
      // Normalizar dados
      const opsNormalizadas = (response.data || []).map(op => ({
        ...op,
        categoria: op.categoria || op.categoria_nome || '',
        tempo_standard: op.tempo_standard || op.tempo_padrao_segundos || 0,
        is_contagem: op.is_contagem === true  // Normalizar para boolean
      }));
      setOperacoes(opsNormalizadas);
    } catch (error) {
      console.error('Erro ao carregar operações:', error);
    }
  }, []);

  const loadCategorias = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categorias`);
      setCategorias(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  }, []);

  const loadOperadores = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/operadores`);
      const ops = response.data || [];
      setOperadores(ops);
      
      // Carregar permissões de cada operador
      const permissoes = {};
      const operadoresFiltrados = ops.filter(o => o.perfil === 'operador');
      
      await Promise.all(operadoresFiltrados.map(async (op) => {
        try {
          const permRes = await axios.get(`${API}/operadores/${op.id}/permissoes`);
          const data = permRes.data;
          
          if (data && data.permissoes_categorias) {
            const permList = [];
            for (const [catId, opIds] of Object.entries(data.permissoes_categorias)) {
              for (const opId of opIds) {
                permList.push({ operacao_id: opId, categoria_id: catId });
              }
            }
            permissoes[op.id] = permList;
          } else if (Array.isArray(data)) {
            permissoes[op.id] = data;
          } else {
            permissoes[op.id] = [];
          }
        } catch {
          permissoes[op.id] = [];
        }
      }));
      
      setPermissoesOperadores(permissoes);
    } catch (error) {
      console.error('Erro ao carregar operadores:', error);
    }
  }, []);

  const loadModelos = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/modelos`);
      setModelos(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar modelos:', error);
    }
  }, []);

  const loadClientes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/clientes`);
      setClientes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  }, []);

  const loadTags = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tags`);
      setTags(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
    }
  }, []);

  const loadAll = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      await Promise.all([
        loadOfs(),
        loadOperacoes(),
        loadCategorias(),
        loadOperadores(),
        loadModelos(),
        loadClientes(),
        loadTags()
      ]);
      setInitialLoadDone(true);
    } finally {
      setLoading(false);
    }
  }, [loading, loadOfs, loadOperacoes, loadCategorias, loadOperadores, loadModelos, loadClientes, loadTags]);

  // Carregar dados iniciais automaticamente
  useEffect(() => {
    if (!initialLoadDone) {
      loadAll();
    }
  }, [initialLoadDone, loadAll]);

  // Ações de CRUD
  const createOF = useCallback(async (data) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/ofs`, data);
      setOfs(prev => [...prev, response.data]);
      toast.success('Ordem de fabrico criada com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao criar OF:', error);
      toast.error('Erro ao criar ordem de fabrico');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateOF = useCallback(async (id, data) => {
    try {
      setLoading(true);
      const response = await axios.put(`${API}/ofs/${id}`, data);
      setOfs(prev => prev.map(of => of.id === id ? response.data : of));
      toast.success('Ordem de fabrico atualizada');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar OF:', error);
      toast.error('Erro ao atualizar ordem de fabrico');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteOF = useCallback(async (id) => {
    try {
      setLoading(true);
      await axios.delete(`${API}/ofs/${id}`);
      setOfs(prev => prev.filter(of => of.id !== id));
      toast.success('Ordem de fabrico eliminada');
    } catch (error) {
      console.error('Erro ao eliminar OF:', error);
      toast.error('Erro ao eliminar ordem de fabrico');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Verificar se operador tem permissão
  const operadorTemPermissao = useCallback((operadorId, operacaoId, categoriaId = null) => {
    const permissoes = permissoesOperadores[operadorId];
    if (!permissoes || !Array.isArray(permissoes) || permissoes.length === 0) return false;
    
    if (categoriaId) {
      return permissoes.some(p => p.operacao_id === operacaoId && p.categoria_id === categoriaId);
    }
    
    return permissoes.some(p => p.operacao_id === operacaoId);
  }, [permissoesOperadores]);

  // Filtrar operadores por permissão
  const filtrarOperadoresPorPermissao = useCallback((operacaoId, categoriaId = null) => {
    return operadores
      .filter(op => op.perfil === 'operador')
      .filter(op => operadorTemPermissao(op.id, operacaoId, categoriaId));
  }, [operadores, operadorTemPermissao]);

  // Verificar se existe operador com permissão
  const existeOperadorComPermissao = useCallback((operacaoId, categoriaId = null) => {
    return operadores
      .filter(op => op.perfil === 'operador')
      .some(op => operadorTemPermissao(op.id, operacaoId, categoriaId));
  }, [operadores, operadorTemPermissao]);

  // Operações selecionadas
  const toggleOperacao = useCallback((operacao) => {
    setSelectedOperacoes(prev => {
      const exists = prev.find(op => op.operacao_id === operacao.id);
      if (exists) {
        return prev.filter(op => op.operacao_id !== operacao.id);
      }
      return [...prev, {
        operacao_id: operacao.id,
        codigo: operacao.codigo,
        nome: operacao.nome,
        tempo_unitario: operacao.tempo_standard || 30,
        ordem: prev.length + 1,
        operadores: []
      }];
    });
  }, []);

  const updateOperacaoTempo = useCallback((operacaoId, delta) => {
    setSelectedOperacoes(prev => 
      prev.map(op => 
        op.operacao_id === operacaoId 
          ? { ...op, tempo_unitario: Math.max(1, (op.tempo_unitario || 30) + delta) }
          : op
      )
    );
  }, []);

  const reorderOperacoes = useCallback((newOrder) => {
    setSelectedOperacoes(newOrder.map((op, idx) => ({ ...op, ordem: idx + 1 })));
  }, []);

  const clearSelectedOperacoes = useCallback(() => {
    setSelectedOperacoes([]);
  }, []);

  // OFs filtradas
  const filteredOfs = useMemo(() => {
    let result = [...ofs];
    
    if (filtros.searchTerm) {
      const term = filtros.searchTerm.toLowerCase();
      result = result.filter(of => 
        of.numero_of?.toLowerCase().includes(term) ||
        of.artigo?.toLowerCase().includes(term) ||
        of.marca?.toLowerCase().includes(term)
      );
    }
    
    if (filtros.estadoFilter !== 'todos') {
      result = result.filter(of => of.estado_of === filtros.estadoFilter);
    }
    
    if (filtros.marcaFilter !== 'todos') {
      result = result.filter(of => of.marca === filtros.marcaFilter);
    }
    
    // Sort
    result.sort((a, b) => {
      let valA = a[filtros.sortBy];
      let valB = b[filtros.sortBy];
      
      if (typeof valA === 'string') {
        valA = valA.toLowerCase();
        valB = valB?.toLowerCase() || '';
      }
      
      if (filtros.sortDirection === 'asc') {
        return valA > valB ? 1 : -1;
      }
      return valA < valB ? 1 : -1;
    });
    
    return result;
  }, [ofs, filtros]);

  // Marcas únicas para o filtro
  const uniqueMarcas = useMemo(() => {
    const marcas = new Set(ofs.map(of => of.marca).filter(Boolean));
    return Array.from(marcas).sort();
  }, [ofs]);

  // ==========================================
  // FUNÇÕES DE GESTÃO DE LOTES
  // ==========================================

  // Carregar lotes de uma OF específica
  const loadLotes = useCallback(async (ofId) => {
    try {
      const response = await axios.get(`${API}/ofs/${ofId}/lotes`);
      return {
        lotes: response.data.lotes || [],
        resumo: response.data.resumo || {}
      };
    } catch (error) {
      console.error('Erro ao carregar lotes:', error);
      toast.error('Erro ao carregar lotes');
      throw error;
    }
  }, []);

  // Criar novo lote
  const createLote = useCallback(async (ofId, loteData) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/ofs/${ofId}/lotes`, loteData);
      toast.success('Lote criado com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao criar lote:', error);
      const detail = error.response?.data?.detail || 'Erro ao criar lote';
      toast.error(detail);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Aprovar lote
  const aprovarLote = useCallback(async (ofId, loteId) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/ofs/${ofId}/lotes/${loteId}/aprovar`);
      toast.success('Lote aprovado com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao aprovar lote:', error);
      toast.error('Erro ao aprovar lote');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Eliminar lote
  const deleteLote = useCallback(async (ofId, loteId) => {
    try {
      setLoading(true);
      await axios.delete(`${API}/ofs/${ofId}/lotes/${loteId}`);
      toast.success('Lote eliminado com sucesso');
    } catch (error) {
      console.error('Erro ao eliminar lote:', error);
      toast.error('Erro ao eliminar lote');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Unir lotes
  const unirLotes = useCallback(async (ofId, lotesIds, quantidades, categoriaDestinoId, observacoes = '') => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/ofs/${ofId}/lotes/unir`, {
        lotes_ids: lotesIds,
        quantidades: quantidades,
        categoria_id: categoriaDestinoId,
        observacoes
      });
      toast.success('Lotes unidos com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao unir lotes:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao unir lotes';
      toast.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Avançar lote para próxima categoria
  const avancarLote = useCallback(async (ofId, loteId, categoriaDestinoId, quantidade, observacoes = '') => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/ofs/${ofId}/lotes/${loteId}/avancar`, {
        categoria_destino_id: categoriaDestinoId,
        quantidade: parseInt(quantidade),
        observacoes
      });
      toast.success('Lote avançado com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao avançar lote:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao avançar lote';
      toast.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Dividir lote
  const dividirLote = useCallback(async (ofId, loteId, categoriaDestinoId, quantidadePorLote, numLotes, quantidadeTotal, observacoes = '') => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/ofs/${ofId}/lotes/${loteId}/dividir-avancado`, {
        categoria_destino_id: categoriaDestinoId,
        quantidade_por_lote: parseInt(quantidadePorLote),
        numero_lotes: parseInt(numLotes),
        quantidade_total: parseInt(quantidadeTotal),
        observacoes
      });
      toast.success('Lote dividido com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao dividir lote:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao dividir lote';
      toast.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Retroceder lote
  const retrocederLote = useCallback(async (ofId, loteId, categoriaDestinoId, quantidade, motivo = '') => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/ofs/${ofId}/lotes/${loteId}/retroceder`, {
        categoria_destino_id: categoriaDestinoId,
        quantidade: parseInt(quantidade),
        motivo
      });
      toast.success('Lote retrocedido com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao retroceder lote:', error);
      toast.error('Erro ao retroceder lote');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Atualizar observações do lote
  const updateLoteObservacoes = useCallback(async (ofId, loteId, observacoes) => {
    try {
      setLoading(true);
      await axios.put(`${API}/ofs/${ofId}/lotes/${loteId}/observacoes`, { observacoes });
      toast.success('Observações atualizadas');
    } catch (error) {
      console.error('Erro ao atualizar observações:', error);
      toast.error('Erro ao atualizar observações');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Obter URL do PDF do lote
  const getLotePdfUrl = useCallback((ofId, loteId) => {
    return `${API}/ofs/${ofId}/lotes/${loteId}/pdf`;
  }, []);

  const value = {
    // Estados
    ofs,
    operacoes,
    categorias,
    categoriasEspeciais,
    operadores,
    modelos,
    clientes,
    tags,
    loading,
    initialLoadDone,
    selectedOF,
    selectedOperacoes,
    filtros,
    filteredOfs,
    uniqueMarcas,
    permissoesOperadores,
    
    // Setters
    setOfs,
    setSelectedOF,
    setSelectedOperacoes,
    setFiltros,
    
    // Ações de Load
    loadAll,
    loadOfs,
    loadOperacoes,
    loadCategorias,
    loadOperadores,
    loadModelos,
    loadClientes,
    loadTags,
    
    // Ações de CRUD OFs
    createOF,
    updateOF,
    deleteOF,
    
    // Ações de CRUD Lotes
    loadLotes,
    createLote,
    aprovarLote,
    deleteLote,
    unirLotes,
    avancarLote,
    dividirLote,
    retrocederLote,
    updateLoteObservacoes,
    getLotePdfUrl,
    
    // Operações selecionadas
    toggleOperacao,
    updateOperacaoTempo,
    reorderOperacoes,
    clearSelectedOperacoes,
    
    // Permissões
    operadorTemPermissao,
    filtrarOperadoresPorPermissao,
    existeOperadorComPermissao
  };

  return (
    <OFContext.Provider value={value}>
      {children}
    </OFContext.Provider>
  );
}

export function useOF() {
  const context = useContext(OFContext);
  if (!context) {
    throw new Error('useOF deve ser usado dentro de um OFProvider');
  }
  return context;
}

export default OFContext;
